/**
 * Bondruck und Kassenlade über die native Bridge der Android-App – Gegenstück zu
 * `druckeBeleg`/`oeffneLade` in `../bondruck.ts` (Weg über Server und Hardware-Bridge), mit
 * derselben Rückmeldung (`GeraeteRueckmeldung`), damit die Oberfläche beide Wege gleich zeigt.
 *
 * Regeln wie im heutigen Weg:
 * - Es entsteht nie ein neuer Beleg; gedruckt wird der vorhandene Beleg mit dem Text des Servers.
 * - Kopie, wenn der Beleg auf diesem Gerät schon (vielleicht) gedruckt wurde.
 * - „Ergebnis unklar“ wird nie automatisch wiederholt; ein Nachdruck trägt dann „KOPIE“.
 * - Aufgerufen wird nur aus Bedienhandlungen der Kasse (Verkauf abschließen, Nachdruck,
 *   Schublade); die App prüft zusätzlich, dass kurz vorher bedient wurde.
 */
import type { Speicher } from '../belegnummern';
import { istGedruckt, merkeGedruckt, type GeraeteRueckmeldung } from '../bondruck';
import { alsGeraetFehler, CODE_ANDERER_INHALT, type BilanxGeraet, type Bonmodell } from './bridge';
import { bonmodellAusDetails } from './bonmodell';
import {
  druckversuchAbgelehntText,
  meldungAusFehler,
  type DruckversuchQuelle,
  type Druckversuch,
} from './druckversuch';
import { schluesselAutoDruck, schluesselNachdruck } from './schluessel';

/**
 * Liefert den Bon eines vorhandenen Belegs, so wie ihn der Server gebildet hat.
 *
 * Vertrag: Eine Bonquelle liest nur. Sie darf am Server **keinen** Druck- oder Geräteauftrag
 * anlegen, sonst könnte eine Hardware-Bridge denselben Bon zusätzlich drucken (zwei
 * Transporte). Deshalb gibt es keine Quelle über `POST …/druckauftrag` (Codex Runde 2, Fund 3).
 */
export type BonQuelle = (
  kassenId: string,
  belegnummer: string,
  opt: { kopie: boolean },
) => Promise<Bonmodell>;

/**
 * Pfad des reinen Lese-Abrufs. **Veraltet:** S-1 ist als Druckversuchsregister umgesetzt
 * (`druckversuch.ts`); ein reiner GET ersetzt die atomare Original/Kopie-Entscheidung nicht
 * (Codex Runde 3, N6). Bleibt nur für bestehende Aufrufer stehen.
 */
export function bonmodellPfad(kassenId: string, belegnummer: string, kopie: boolean): string {
  return `/kassen/${encodeURIComponent(kassenId)}/belege/${encodeURIComponent(belegnummer)}/bonmodell${kopie ? '?kopie=1' : ''}`;
}

/**
 * Bonquelle über `GET /kassen/:id/belege/:nr/bonmodell` (S-1): Antwort `{ zeilen, qrCode?, kopie? }`.
 * [holeJson] führt ein GET mit dem Kassenschlüssel aus (z. B. eine Lesemethode der KassenApi).
 * Eine Antwort, die nach einem Geräteauftrag aussieht (`auftragId`), wird verworfen.
 */
export function bonQuelleUeberBonmodellAbruf(
  holeJson: (pfad: string) => Promise<unknown>,
): BonQuelle {
  return async (kassenId, belegnummer, opt) => {
    const antwort = await holeJson(bonmodellPfad(kassenId, belegnummer, opt.kopie));
    if (typeof antwort !== 'object' || antwort === null || 'auftragId' in antwort) {
      throw new Error('Server lieferte keinen druckbaren Bon');
    }
    const bon = bonmodellAusDetails(antwort as Record<string, unknown>);
    if (bon === null) {
      throw new Error('Server lieferte keinen druckbaren Bon');
    }
    return bon;
  };
}

/**
 * Genau ein Transport je Bon: nativ nur mit Geräte-Bridge UND reiner Bonquelle, sonst der
 * heutige Weg über Server und Hardware-Bridge – nie beide für denselben Druck.
 */
export function druckweg(
  geraet: BilanxGeraet | null,
  quelle: BonQuelle | DruckversuchQuelle | null,
): 'nativ' | 'bridge' {
  return geraet !== null && quelle !== null ? 'nativ' : 'bridge';
}

export interface DruckOptionen {
  /** `auto`: einmaliger automatischer Druck nach dem Verkauf; `nachdruck`: Taste. */
  art: 'auto' | 'nachdruck';
  /** Zufallsteil für den Nachdruck (Tests). */
  zufall?: string;
}

function mitHinweis(text: string, hinweis: unknown): string {
  return typeof hinweis === 'string' && hinweis !== '' ? `${text} (${hinweis})` : text;
}

/** 409-Codes des Servers, die einen früheren Versuch zu diesem Beleg belegen. */
const VORHERIGER_VERSUCH: ReadonlySet<string> = new Set([
  'versuch_abgeschlossen',
  'versuch_anderes_geraet',
  'schluessel_anderer_inhalt',
]);

function istDruckversuchQuelle(q: BonQuelle | DruckversuchQuelle): q is DruckversuchQuelle {
  return typeof q !== 'function';
}

/**
 * Beleg über die native Bridge drucken; wirft nie. [quelle]: das S-1-Druckversuchsregister
 * (`druckversuchQuelle`, Server entscheidet Original/Kopie und bekommt das Ergebnis gemeldet)
 * oder eine reine Bonquelle (veraltet).
 */
export async function druckeBelegAmGeraet(
  geraet: BilanxGeraet,
  quelle: BonQuelle | DruckversuchQuelle,
  speicher: Speicher,
  kassenId: string,
  belegnummer: string,
  opt: DruckOptionen,
): Promise<GeraeteRueckmeldung> {
  const lokalGedruckt = istGedruckt(speicher, kassenId, belegnummer);
  if (opt.art === 'auto' && lokalGedruckt) {
    // Der automatische Druck lief hier schon (gedruckt oder unklar). Nicht mit anderem Inhalt
    // (Kopie) unter demselben Schlüssel erneut anfordern (Codex Runde 3, N9); eine Kopie gibt es
    // nur über die Nachdruck-Taste mit neuem Schlüssel. Fehlt der Merker (Speicherfehler, anderes
    // Fenster), schützt der gemeinsame Auto-Schlüssel je Beleg in der App und am Server.
    return {
      ok: null,
      text: 'Bon wurde für diesen Beleg schon gedruckt oder versucht – bei Bedarf Nachdruck (trägt „KOPIE“).',
    };
  }
  const schluessel =
    opt.art === 'auto'
      ? schluesselAutoDruck(kassenId, belegnummer)
      : schluesselNachdruck(kassenId, belegnummer, opt.zufall);

  let bon: Bonmodell;
  let versuch: Druckversuch | null = null;
  if (istDruckversuchQuelle(quelle)) {
    const r = await quelle.reserviere(kassenId, belegnummer, { schluessel, kopie: lokalGedruckt });
    if (!r.ok) {
      // Jede Abweisung (409, Netz, ungültige Antwort): nicht drucken
      if (VORHERIGER_VERSUCH.has(r.code)) {
        // Unter diesem Beleg lief schon ein Versuch (anderes Fenster, Neuladen, anderes Gerät):
        // lokal als gedruckt merken – die Nachdruck-Taste fordert dann ausdrücklich eine Kopie an.
        merkeGedruckt(speicher, kassenId, belegnummer);
      }
      const text = druckversuchAbgelehntText(r.code, r.text);
      return {
        ok: false,
        text: `Bon nicht gedruckt: ${text}${text.includes('Nachdruck') ? '' : ' – Nachdruck möglich'}`,
      };
    }
    versuch = r.versuch;
    bon = r.versuch.bon;
  } else {
    try {
      bon = await quelle(kassenId, belegnummer, { kopie: lokalGedruckt });
    } catch (e) {
      return {
        ok: false,
        text: `Bon nicht gedruckt: Belegtext nicht abrufbar (${e instanceof Error ? e.message : String(e)}) – Nachdruck möglich`,
      };
    }
  }
  // Im Zweifel Kopie: lieber „KOPIE“ auf einem Original als ein ungekennzeichnetes Duplikat.
  // Beim Druckversuch entscheidet der Server; lokal wurde `kopie` schon als Verschärfung gesendet.
  const kopie = versuch !== null ? versuch.kopie : lokalGedruckt || bon.kopie === true;
  const zuDrucken: Bonmodell = versuch !== null ? bon : kopie ? { ...bon, kopie: true } : bon;
  const name = kopie ? 'Kopie' : 'Bon';
  const melde = (m: Parameters<DruckversuchQuelle['melde']>[4]): Promise<void> =>
    versuch !== null && istDruckversuchQuelle(quelle)
      ? quelle.melde(kassenId, belegnummer, versuch.versuchId, versuch.inhaltHash, m)
      : Promise.resolve();
  try {
    const r = await geraet.drucke(zuDrucken, schluessel);
    merkeGedruckt(speicher, kassenId, belegnummer);
    await melde({ ergebnis: 'gedruckt' });
    if (r.wiederholt === true) {
      return { ok: true, text: `${name} wurde bereits gedruckt – nicht erneut gedruckt` };
    }
    return { ok: true, text: mitHinweis(`${name} gedruckt`, r.hinweis) };
  } catch (e) {
    const f = alsGeraetFehler(e);
    await melde(meldungAusFehler(f));
    if (f.code === CODE_ANDERER_INHALT) {
      // Jetzt nichts gedruckt, aber unter diesem Schlüssel lief schon ein Versuch (z. B. Original,
      // jetzt verlangte der Server Kopie): vielleicht gedruckt, weitere Drucke sind Kopien.
      merkeGedruckt(speicher, kassenId, belegnummer);
      return {
        ok: null,
        text: `${name} nicht erneut gedruckt: Für diesen Beleg lief schon ein automatischer Druck. Ausdruck prüfen; ein Nachdruck trägt „KOPIE“.`,
      };
    }
    if (f.unklar) {
      // Vielleicht gedruckt: der nächste Druck ist eine Kopie
      merkeGedruckt(speicher, kassenId, belegnummer);
      return {
        ok: null,
        text: `${name}: Ergebnis unklar – ${f.text}. Nur nachdrucken, wenn nichts herauskam (Nachdruck trägt „KOPIE“).`,
      };
    }
    if (f.wiederholt) {
      // gespeicherter früherer Fehler: Schlüssel verbraucht, nur ein Nachdruck (neuer Schlüssel) hilft
      return {
        ok: false,
        text: `${name} nicht gedruckt: ${f.text} (früherer Versuch) – Nachdruck möglich`,
      };
    }
    return { ok: false, text: `${name} nicht gedruckt: ${f.text} – Nachdruck möglich` };
  }
}

/** Kassenlade über die native Bridge öffnen; wirft nie. */
export async function oeffneLadeAmGeraet(
  geraet: BilanxGeraet,
  schluessel: string,
): Promise<GeraeteRueckmeldung> {
  try {
    const r = await geraet.oeffneLade(schluessel);
    if (r.wiederholt === true) {
      return { ok: true, text: 'Kassenlade wurde für diesen Vorgang bereits geöffnet' };
    }
    return { ok: true, text: 'Kassenlade geöffnet' };
  } catch (e) {
    const f = alsGeraetFehler(e);
    if (f.unklar) {
      return {
        ok: null,
        text: `Kassenlade: Ergebnis unklar – ${f.text}. Falls zu, Lade mit dem Schlüssel öffnen.`,
      };
    }
    return {
      ok: false,
      text: f.wiederholt
        ? `Kassenlade nicht geöffnet: ${f.text} (früherer Versuch)`
        : `Kassenlade nicht geöffnet: ${f.text}`,
    };
  }
}
